import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  Shield,
  Star,
  MapPin,
  Sparkles,
  Camera,
  Cpu,
  BookOpen,
  Compass,
} from "lucide-react";
import { useEffect } from "react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const CATEGORY_ICONS = [
  { Icon: MapPin, label: "Google Business Profile" },
  { Icon: Star, label: "Reviews" },
  { Icon: Shield, label: "Website Trust" },
  { Icon: Compass, label: "Local Authority" },
  { Icon: Sparkles, label: "AI Visibility" },
  { Icon: Camera, label: "Proof" },
  { Icon: BookOpen, label: "Educational Content" },
  { Icon: Cpu, label: "Technical Trust" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      {/* Top marker bar */}
      <header className="border-b border-[#333C47]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 bg-[#6CC24A]" />
            <div className="flex items-baseline gap-2">
              <span className="font-display font-bold tracking-tight">PULSE POINT</span>
              <span className="text-[#6CC24A] font-display font-bold tracking-tight">TRUST SCORE</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://officialpulsepoint.com"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline text-xs text-[#C7CBD1] hover:text-white"
              data-testid="landing-main-site-link"
            >
              officialpulsepoint.com
            </a>
            <button
              onClick={startLogin}
              className="pp-btn-primary text-sm"
              data-testid="landing-signin-top"
            >
              Sign in
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pp-grid-bg opacity-40" />
        <div className="relative max-w-[1360px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="pp-badge-green mb-6">
              <span className="w-1.5 h-1.5 bg-[#6CC24A] rounded-full" />
              A Pulse Point Marketing tool
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[68px] leading-[1.02] tracking-tight text-white">
              Your work already earns trust in person.
              <span className="block text-[#6CC24A] mt-3">
                Let's measure how it looks online.
              </span>
            </h1>
            <p className="mt-8 text-lg text-[#C7CBD1] max-w-xl leading-relaxed">
              Pulse Point Trust Score is a professional trust assessment for local
              service businesses. In thirty seconds, see how trustworthy your
              business looks across Google Search, Google Maps, AI answers, and
              your own website — and exactly what to fix first.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={startLogin}
                data-testid="landing-signin-hero"
                className="pp-btn-primary flex items-center gap-2"
              >
                Start free trust audit <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="#how"
                className="pp-btn-secondary"
                data-testid="landing-learn-more"
              >
                How it works
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[#C7CBD1]">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#6CC24A]" />
                Founder-led · USMC veteran-owned
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#6CC24A]" />
                Evidence, not opinion
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#6CC24A]" />
                No credit card required
              </div>
            </div>
          </div>

          {/* Score preview mock */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="pp-card p-8 relative"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="pp-label">Trust Assessment</div>
                <div className="font-display font-bold text-white text-lg mt-1">
                  Tri-County Plumbing
                </div>
                <div className="text-xs text-[#C7CBD1]">tricountyplumbing.example</div>
              </div>
              <div className="pp-badge-green">Live sample</div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              <div>
                <div className="pp-label mb-2">Trust score</div>
                <div className="metric text-6xl font-bold text-white leading-none">
                  31
                  <span className="text-2xl text-[#C7CBD1]"> / 100</span>
                </div>
                <div className="mt-3 h-1.5 bg-[#1A1F24] border border-[#333C47]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "31%" }}
                    transition={{ delay: 0.6, duration: 1.2 }}
                    className="h-full bg-[#EF4444]"
                  />
                </div>
                <div className="mt-3 text-xs text-[#F87171]">
                  Losing trust before customers ever contact you.
                </div>
              </div>
              <div>
                <div className="pp-label mb-2">Assessment coverage</div>
                <div className="metric text-4xl font-bold text-white">
                  38%
                </div>
                <div className="mt-3 text-xs text-[#C7CBD1]">
                  3 of 8 trust areas measured. Unmeasured areas are excluded
                  from the score entirely — never counted against them.
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-[#333C47] pt-4 space-y-2.5">
              <div className="text-xs pp-label">Biggest trust leaks</div>
              {[
                { t: "Google Business Profile hasn't been updated in 54 days", i: 12 },
                { t: "Only 14 Google reviews — competitors average 148", i: 14 },
                { t: "No trust badges above the fold on the homepage", i: 8 },
              ].map((leak, i) => (
                <div key={i} className="flex items-start justify-between gap-3">
                  <div className="text-sm text-white leading-snug">{leak.t}</div>
                  <div className="pp-badge-danger shrink-0">−{leak.i} pts</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Belief bar */}
      <section className="border-y border-[#333C47] bg-[#0F1115]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <div className="pp-label mb-3">The core belief</div>
            <div className="font-display text-3xl md:text-4xl leading-tight text-white max-w-2xl">
              Businesses don't lose customers because they're bad. They lose
              customers because strangers <span className="text-[#6CC24A]">don't trust them yet</span>.
            </div>
          </div>
          <div className="text-sm text-[#C7CBD1] leading-relaxed">
            Every recommendation in this platform is built to close the gap between
            the work you already do and the trust a stranger sees before deciding
            whether to call you.
          </div>
        </div>
      </section>

      {/* What we score */}
      <section id="how" className="py-20">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="mb-12 flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="pp-label mb-3">What we score</div>
              <h2 className="font-display text-4xl md:text-5xl text-white max-w-2xl leading-tight">
                Eight trust categories, one honest number.
              </h2>
            </div>
            <div className="text-sm text-[#C7CBD1] max-w-sm">
              The score isn't a marketing gimmick. It's a weighted read of the same
              trust signals Google, Maps and AI assistants use to decide who to
              recommend.
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORY_ICONS.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="pp-card p-6"
                data-testid={`landing-category-${i}`}
              >
                <div className="w-9 h-9 border border-[#333C47] bg-[#1A1F24] flex items-center justify-center">
                  <c.Icon className="h-4 w-4 text-[#6CC24A]" />
                </div>
                <div className="mt-4 font-display font-semibold text-white">
                  {c.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#333C47] py-20 bg-[#0F1115]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="pp-label mb-3">Ready when you are</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight">
              See what a stranger sees, before they decide.
            </h2>
            <p className="mt-6 text-[#C7CBD1] max-w-xl leading-relaxed">
              Sign in with Google. Give us your business name and website. In under
              thirty seconds you'll see your score, your biggest leaks, and the
              exact steps to fix them. No credit card, no obligation.
            </p>
          </div>
          <div className="pp-card p-8">
            <div className="pp-label mb-3">Sign in</div>
            <div className="font-display text-2xl text-white leading-tight">
              One click. One honest picture of where you stand.
            </div>
            <button
              onClick={startLogin}
              data-testid="landing-signin-cta"
              className="mt-6 w-full pp-btn-primary flex items-center justify-center gap-2"
            >
              Continue with Google <ArrowRight className="h-4 w-4" />
            </button>
            <div className="mt-3 text-xs text-[#C7CBD1]">
              Your business info is never sold or shared.
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#333C47] py-8">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-[#C7CBD1]">
            © {new Date().getFullYear()} Pulse Point Marketing · Williamstown, NJ
          </div>
          <div className="text-xs text-[#C7CBD1]">
            Founder-led · USMC veteran-owned · Selective partnerships
          </div>
        </div>
      </footer>
    </div>
  );
}
